import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please enter email and password");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back, admin");
      nav("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid credentials";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream grid lg:grid-cols-2" data-testid="admin-login-page">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 md:px-16 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-wine transition-colors mb-10"
          data-testid="admin-login-home-link"
        >
          <ArrowLeft size={16} />
          Back to Yuukke
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img src="/yuukke-logo.png" alt="Yuukke" className="h-9 w-auto" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-wine border-l border-brand-line pl-3">
            admin
          </span>
        </div>

        <h1 className="font-serif text-4xl md:text-5xl font-medium text-brand-ink leading-tight">
          Curation <span className="italic text-brand-wine">Console</span>
        </h1>
        <p className="mt-3 text-brand-muted text-[15px] max-w-md">
          Sign in to review submitted businesses, view AI-generated readiness scores, and curate the Yuukke Business Exchange.
        </p>

        <form onSubmit={submit} noValidate className="mt-10 space-y-4 max-w-md" data-testid="admin-login-form">
          <label className="block">
            <span className="text-[13px] font-medium text-brand-ink mb-1.5 block">Email</span>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@yuukke.com"
                className="w-full bg-white border border-brand-line rounded-xl pl-11 pr-4 py-3 h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all"
                data-testid="admin-email-input"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-[13px] font-medium text-brand-ink mb-1.5 block">Password</span>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="w-full bg-white border border-brand-line rounded-xl pl-11 pr-4 py-3 h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all"
                data-testid="admin-password-input"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-wine text-white rounded-lg py-4 font-semibold hover:bg-brand-wineDark hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            data-testid="admin-login-submit"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        <p className="mt-8 text-xs text-brand-muted max-w-md">
          For authorised curation team members only. All actions are logged.
        </p>
      </div>

      {/* Right: editorial visual */}
      <div className="hidden lg:flex bg-brand-wine text-brand-cream p-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="relative">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-cream/70">
            Curation Console
          </span>
          <h2 className="mt-4 font-serif text-5xl font-medium leading-tight">
            Every listing on Yuukke is <span className="italic">reviewed with care.</span>
          </h2>
          <p className="mt-6 text-brand-cream/80 leading-relaxed max-w-md">
            AI-powered readiness scoring highlights gaps. You approve the businesses that truly deserve to be discovered.
          </p>
        </div>
        <div className="relative flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center font-serif text-2xl">
            AI
          </div>
          <div>
            <p className="font-serif text-2xl">Claude Sonnet 4.5</p>
            <p className="text-brand-cream/70 text-sm">Powers readiness scoring & profile generation</p>
          </div>
        </div>
      </div>
    </div>
  );
}
